import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";

const THEME_GRADIENTS = [
  "linear-gradient(135deg,#5B3FE6,#9B6DFF)",
  "linear-gradient(135deg,#FF6B9D,#FFA0C0)",
  "linear-gradient(135deg,#0BA888,#087A63)",
  "linear-gradient(135deg,#FF8848,#FFAE7B)",
  "linear-gradient(135deg,#FFD23F,#FFA200)",
  "linear-gradient(135deg,#7C5BFA,#B89BFF)",
  "linear-gradient(135deg,#FF2E63,#E54A2A)",
] as const;

const EMOJIS = ["🐻", "💄", "⚽", "🎧", "🎒", "📷", "👟"] as const;

/**
 * Each theme maps to a keyword that will be passed to the catalog search,
 * so clicking a theme card pre-filters the catalog to relevant products.
 * channel defaults to taobao for fashion/lifestyle themes; 1688 for electronics.
 */
const THEME_CATALOG_PARAMS: Array<{ keyword: string; channel: string }> = [
  { keyword: "Pop Mart", channel: "taobao" },        // theme1 hero
  { keyword: "国货美妆", channel: "taobao" },          // theme2
  { keyword: "户外运动", channel: "taobao" },          // theme3
  { keyword: "降噪耳机", channel: "1688" },            // theme4
  { keyword: "书包", channel: "taobao" },              // theme5 开学季
  { keyword: "运动相机", channel: "taobao" },          // theme6 Vlog
  { keyword: "球鞋", channel: "taobao" },              // theme7
];

export function ShopwindowPage() {
  const { t } = useTranslation("shopwindow");
  const navigate = useNavigate();

  function handleEditorialCta() {
    // Editorial is about buying from 1688 at wholesale prices;
    // navigate to catalog pre-filled with a relevant 1688 keyword search.
    navigate({
      to: "/catalog" as never,
      search: { keyword: "1688批发", channel: "1688" } as never,
    });
  }

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("breadcrumb.shopwindow")} back />

      <div style={{ background: "var(--gradient-sky)", padding: 16, textAlign: "center" }}>
        <span
          style={{
            display: "inline-block",
            padding: "3px 10px",
            background: "rgba(255,255,255,.7)",
            color: "var(--color-primary-fill)",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {t("head.badge")}
        </span>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 22,
            fontWeight: 900,
            margin: "8px 0 0",
            letterSpacing: "-0.015em",
          }}
        >
          {t("head.hero_title")}{" "}
          <span style={{ color: "var(--color-cta)" }}>{t("head.hero_accent")}</span>
        </h1>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        {/* Hero theme */}
        <Link
          to={"/catalog" as never}
          search={THEME_CATALOG_PARAMS[0] as never}
          style={{
            background: THEME_GRADIENTS[0],
            color: "#fff",
            borderRadius: 14,
            padding: 18,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            textDecoration: "none",
          }}
        >
          <div
            style={{ position: "absolute", fontSize: 140, right: -30, bottom: -40, opacity: 0.9 }}
          >
            {EMOJIS[0]}
          </div>
          <div style={{ position: "relative", zIndex: 1 }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                opacity: 0.85,
                letterSpacing: "0.06em",
              }}
            >
              {t("theme1.label")}
            </span>
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 18,
                fontWeight: 900,
                margin: "6px 0 4px",
                lineHeight: 1.15,
                color: "#fff",
              }}
            >
              {t("theme1.title")}
            </h3>
            <div style={{ fontSize: 10, opacity: 0.85 }}>{t("theme1.desc")}</div>
          </div>
        </Link>

        {/* Theme grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {([2, 3, 4, 5, 6, 7] as const).map((n) => {
            const dark = n === 5;
            const params = THEME_CATALOG_PARAMS[n - 1];
            return (
              <Link
                key={n}
                to={"/catalog" as never}
                search={params as never}
                style={{
                  background: THEME_GRADIENTS[n - 1],
                  color: "#fff",
                  borderRadius: 12,
                  padding: 14,
                  textDecoration: "none",
                  position: "relative",
                  overflow: "hidden",
                  minHeight: 100,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    fontSize: 60,
                    right: -10,
                    bottom: -15,
                    opacity: dark ? 0.55 : 0.85,
                  }}
                >
                  {EMOJIS[n - 1]}
                </div>
                <div style={{ position: "relative", zIndex: 1 }}>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 9,
                      opacity: dark ? 0.7 : 0.85,
                    }}
                  >
                    {String(n).padStart(2, "0")}
                  </span>
                  <h4
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 13,
                      fontWeight: 800,
                      margin: "4px 0 0",
                      color: dark ? "var(--color-text)" : "#fff",
                      lineHeight: 1.15,
                    }}
                  >
                    {t(`theme${n}.title` as never)}
                  </h4>
                </div>
                <span style={{ fontSize: 9, opacity: dark ? 0.75 : 0.85, position: "relative" }}>
                  {t(`theme${n}.count` as never)} →
                </span>
              </Link>
            );
          })}
        </div>

        {/* Editorial */}
        <div
          style={{
            background: "linear-gradient(135deg,var(--color-text),#1A2924)",
            color: "#fff",
            borderRadius: 14,
            padding: 16,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 120,
              height: 120,
              background: "rgba(255,107,74,.2)",
              borderRadius: "50%",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                opacity: 0.7,
                letterSpacing: "0.06em",
              }}
            >
              {t("editorial.label")}
            </span>
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 15,
                fontWeight: 900,
                margin: "6px 0 4px",
                color: "#fff",
              }}
            >
              {t("editorial.title")}
            </h3>
            <p
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,.7)",
                margin: "0 0 10px",
                lineHeight: 1.5,
              }}
            >
              {t("editorial.desc")}
            </p>
            <button
              type="button"
              onClick={handleEditorialCta}
              style={{
                background: "var(--color-cta)",
                color: "#fff",
                border: 0,
                padding: "6px 14px",
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              {t("editorial.cta")}
            </button>
          </div>
        </div>
      </div>
    </TgPage>
  );
}
